import { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import ScrollReveal from "./ScrollReveal";

interface PremiumCTAProps {
  title: string;
  description?: string;
  primaryAction?: {
    label: string;
    href: string;
    icon?: boolean;
  };
  secondaryAction?: {
    label: string;
    href: string;
  };
  variant?: "dark" | "gradient" | "muted" | "primary";
  className?: string;
  children?: ReactNode;
}

const PremiumCTA = ({
  title,
  description,
  primaryAction,
  secondaryAction,
  variant = "dark",
  className,
  children,
}: PremiumCTAProps) => {
  const variantClasses = {
    dark: "bg-hero-premium text-primary-foreground",
    gradient: "bg-gradient-to-r from-primary via-primary/95 to-primary text-primary-foreground",
    muted: "bg-muted",
    primary: "bg-primary text-primary-foreground",
  };

  return (
    <section className={cn("relative py-16 md:py-24 overflow-hidden cta-frame", variantClasses[variant], className)}>
      {/* Decorative Elements */}
      {(variant === "dark" || variant === "gradient" || variant === "primary") && (
        <>
          <div className="absolute top-0 left-0 w-32 h-32 border-l-2 border-t-2 border-secondary/20" />
          <div className="absolute bottom-0 right-0 w-32 h-32 border-r-2 border-b-2 border-secondary/20" />
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-secondary/10 rounded-full blur-3xl -translate-y-1/2" />
            <div className="absolute top-1/2 right-1/4 w-64 h-64 bg-secondary/5 rounded-full blur-3xl -translate-y-1/2" />
          </div>
        </>
      )}

      <div className="container relative">
        <ScrollReveal animation="fade-up" className="text-center max-w-3xl mx-auto">
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            {title}
          </h2>

          {description && (
            <p className={cn(
              "text-lg md:text-xl mb-8 max-w-2xl mx-auto",
              (variant === "dark" || variant === "gradient" || variant === "primary") 
                ? "text-primary-foreground/80" 
                : "text-muted-foreground"
            )}>
              {description}
            </p>
          )}

          {(primaryAction || secondaryAction) && (
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {primaryAction && (
                <Button
                  asChild
                  size="lg"
                  className={cn(
                    "group btn-glow",
                    (variant === "dark" || variant === "gradient" || variant === "primary")
                      ? "bg-secondary hover:bg-secondary/90 text-secondary-foreground"
                      : "bg-secondary hover:bg-secondary/90 text-secondary-foreground"
                  )}
                >
                  <Link to={primaryAction.href}>
                    {primaryAction.label}
                    {primaryAction.icon !== false && (
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    )}
                  </Link>
                </Button>
              )}

              {secondaryAction && (
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className={cn(
                    (variant === "dark" || variant === "gradient" || variant === "primary")
                      ? "bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10"
                      : ""
                  )}
                >
                  <Link to={secondaryAction.href}>{secondaryAction.label}</Link>
                </Button>
              )}
            </div>
          )}

          {children}
        </ScrollReveal>
      </div>
    </section>
  );
};

export default PremiumCTA;
